/**
 * CheckpointCard — appears in the right-rail chat scrollback after a
 * pipeline stage finishes. Renders as plain divs styled by the
 * app-shell.css classes (.checkpoint-card etc.). No MUI; the parent
 * wraps this in a `<div class="msg agent">` so it stretches to the rail
 * width via the `messages > .steered-card` rule.
 *
 * UX overhaul 2026-05: the Continue / Refine / Skip / per-section Refine
 * buttons are gone. The card now appends a prose options line to the
 * summary so the user types the next step ("continue", "refine",
 * "refine segments", etc.) just like any chat input. The agent's
 * SYSTEM_PROMPT already routes those plain-text replies. The button
 * props stay on the signature (callers still pass them) so this stays a
 * drop-in replacement — they're simply unused.
 *
 * Performance: Wrapped in React.memo to prevent re-rendering settled checkpoint
 * cards on every SSE chunk. Re-renders only when props actually change.
 */
import * as React from 'react';
import RichMarkdown from './RichMarkdown';
import SuggestionChips from './SuggestionChips';

export type CheckpointStage =
  | 'brd_extracted'
  | 'data_mapping_extracted'
  | 'segmentation'
  | 'channel'
  | 'decisioning'
  | 'journey'
  | 'qa'
  | 'parallel_design'
  | 'parallel_extraction';

export type ParallelDesignSection =
  | 'segmentation'
  | 'channel'
  | 'decisioning'
  | 'journey';

export interface ParallelSectionStatus {
  status: 'complete' | 'incomplete';
  blockCount?: number;
  s3Key?: string;
}

export type ExtractionSection = 'brd' | 'data_mapping';

export interface ExtractionSectionStatus {
  status: 'done' | 'skipped';
  s3Key?: string;
  filename?: string;
  sheetCount?: number;
}

export interface CheckpointCardProps {
  stage: CheckpointStage;
  title: string;
  summary: string;
  // Kept for API compatibility — callers still pass these. Unused after
  // the UX overhaul; the card renders text-only options and the user
  // types the next step. Removing them would force every call site to
  // change; keeping them lets this be a drop-in replacement.
  onContinue: () => void;
  onRefine: () => void;
  onSkip?: () => void;
  disabled?: boolean;
  onRefineSection?: (section: ParallelDesignSection) => void;
  sectionStatuses?: Record<ParallelDesignSection, ParallelSectionStatus>;
  extractionSectionStatuses?: Record<ExtractionSection, ExtractionSectionStatus>;
  onSendMessage?: (text: string) => void;
}

// 01a/01b for the two extraction stages — both happen before §02 (segments).
const STAGE_INDEX: Record<CheckpointStage, string> = {
  brd_extracted: '01a',
  data_mapping_extracted: '01b',
  segmentation: '02',
  channel: '03',
  decisioning: '04',
  journey: '05',
  qa: '06',
  parallel_design: '02-05',
  parallel_extraction: '01',
};

// 2026-05-29 Chainlit alignment: the next-step prose used to be appended
// here so the card narrated "type `continue` to advance...". The agent
// now produces the conversational ask in its own reply, and we render
// the checkpoint as a flat Chainlit-style step (no card tag, no chip
// row, no duplicate Next: prose). Returns empty string.
function nextStepLine(_stage: CheckpointStage): string {
  return '';
}

// Per-stage suggestion chips for quick actions.
function stageChips(stage: CheckpointStage): string[] {
  switch (stage) {
    case 'brd_extracted':
    case 'data_mapping_extracted':
    case 'parallel_extraction':
      return ['Continue', 'Tell me what was missed', 'Ask a question'];
    case 'parallel_design':
      return ['Continue to QA', 'Refine segments', 'Refine channels', 'Refine decisioning', 'Refine journey'];
    case 'segmentation':
    case 'channel':
    case 'decisioning':
    case 'journey':
      return ['Continue', 'Refine', 'Skip'];
    case 'qa':
      return ['Refine a section', 'Re-run QA', 'Ask a question'];
    default:
      return [];
  }
}

const CheckpointCard = React.memo(function CheckpointCard({
  stage,
  title,
  summary,
  disabled = false,
  sectionStatuses,
  extractionSectionStatuses,
  onSendMessage,
}: CheckpointCardProps) {
  // Combined parallel-extraction card. Appears once after extract_in_parallel
  // completes. Layout: two-line summary derived from sectionStatuses (BRD +
  // data mapping). No buttons — user types `continue`.
  if (stage === 'parallel_extraction') {
    const brd = extractionSectionStatuses?.brd;
    const dm = extractionSectionStatuses?.data_mapping;
    const lines: string[] = [];
    if (brd?.status === 'done') {
      lines.push(`BRD: ${brd.filename || 'extracted'}`);
    }
    if (dm?.status === 'done') {
      const sheetPart = dm.sheetCount ? ` (${dm.sheetCount} sheets)` : '';
      lines.push(`Data mapping: ${dm.filename || 'extracted'}${sheetPart}`);
    }
    const bodySummary = (lines.length > 0 ? lines.join('\n') : summary) + nextStepLine(stage);

    return (
      <div
        className="checkpoint-card checkpoint-card-parallel"
        data-disabled={disabled || undefined}
      >
        <div className="checkpoint-card-title">{title}</div>
        <div className="checkpoint-card-summary">
          <RichMarkdown content={bodySummary} />
        </div>
      </div>
    );
  }

  if (stage === 'parallel_design') {
    // Surface incomplete sections in the summary text so the user knows
    // which `refine <section>` is most urgent. Below the summary we drop
    // the same per-section list as a one-liner if any section is incomplete.
    const incomplete = sectionStatuses
      ? (Object.keys(sectionStatuses) as ParallelDesignSection[])
          .filter((s) => sectionStatuses[s]?.status === 'incomplete')
      : [];
    const incompleteLine = incomplete.length
      ? `\n\n_Incomplete sections:_ ${incomplete.join(', ')} — try \`refine ${incomplete[0]}\` first.`
      : '';
    const bodySummary = summary + incompleteLine + nextStepLine(stage);
    return (
      <div
        className="checkpoint-card checkpoint-card-parallel"
        data-disabled={disabled || undefined}
      >
        <div className="checkpoint-card-title">{title}</div>
        <div className="checkpoint-card-summary"><RichMarkdown content={bodySummary} /></div>
      </div>
    );
  }

  const bodySummary = summary + nextStepLine(stage);
  return (
    <div className="checkpoint-card" data-disabled={disabled || undefined}>
      <div className="checkpoint-card-title">{title}</div>
      <div className="checkpoint-card-summary"><RichMarkdown content={bodySummary} /></div>
    </div>
  );
});

export default CheckpointCard;
